// Frontend: EditPerkataan.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useTheme } from "@emotion/react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Button, Snackbar, Alert } from "@mui/material";

import HeaderLayout from "../../../../components/HeaderLayout";
import InputTextField from "../../../../components/InputTextField";
import SearchableDropdown from "../../../../components/SearchableDropdown";
import FileUpload from "../../../../components/FileUpload";
import FormikSubmitButton from "../../../../components/FormikSubmitButton";
import TextArea from "../../../../components/TextArea";
import api from "../../../../api/apiClient";
import { handleUpload } from "../../../../utils/uploadFile";
import LoadingSpinner from "../../../../components/LoadingSpinner"; // ✅ import spinner

const kategoriBudayaOptions = [
  "Agama",
  "Mental & Emosi",
  "Material",
  "Sosial",
  "Ekologi",
  "Politik",
  "Linguistik",
  "Sastera",
];

const validationSchema = Yup.object()
  .shape({
    kategoribudaya: Yup.string().required("Kategori Budaya is required"),
    perkataan: Yup.string().required("Perkataan is required"),
    ayatalquran: Yup.mixed().required("Ayat Al-Quran is required"),
    denotasi: Yup.string().required("Denotasi is required"),
    konotasi: Yup.string().required("Konotasi is required"),
    terjemahanayat: Yup.string().nullable(),
    terjemahanImage: Yup.mixed().nullable(),
    tafsiranUlama1: Yup.string().required("Tafsiran Ulama 1 is required"),
    tafsiranUlama2: Yup.string().required("Tafsiran Ulama 2 is required"),
  })
  .test(
    "terjemahan-required",
    "Either Terjemahan Ayat text or image is required",
    function (values) {
      const { terjemahanayat, terjemahanImage } = values || {};

      // Pass validation if either one has a value
      const hasText = !!terjemahanayat?.trim();
      const hasImage =
        terjemahanImage && terjemahanImage !== null && terjemahanImage !== "";

      return hasText || hasImage;
    }
  );

export default function EditPerkataan() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const navigateBack = () => navigate(-1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/perkataan/${id}`);
        if (res.data.success) {
          const item = res.data.data;

          setInitialValues({
            kategoribudaya: item.KategoriBudaya || "",
            perkataan: item.Perkataan || "",
            ayatalquran: item.AyatAlQuran || null,
            terjemahanayat: item.TerjemahanAyat || "",
            terjemahanImage: item.TerjemahanImage || null,
            denotasi: item.Denotasi || "",
            konotasi: item.Konotasi || "",
            tafsiranUlama1: item.TafsiranUlama1 || "",
            tafsiranUlama2: item.TafsiranUlama2 || "",
          });
        }
      } catch (err) {
        console.error("Error fetching data:", err.message);
        setSnackbar({
          open: true,
          message: "Failed to load data",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      let ayatUrl = null;
      if (values.ayatalquran) {
        if (values.ayatalquran instanceof File) {
          ayatUrl = await handleUpload(values.ayatalquran, "ayat-videos");
        } else {
          ayatUrl = values.ayatalquran;
        }
      }

      let terjemahanImageUrl = null;
      if (values.terjemahanImage) {
        if (values.terjemahanImage instanceof File) {
          terjemahanImageUrl = await handleUpload(
            values.terjemahanImage,
            "terjemahan-assets"
          );
        } else {
          terjemahanImageUrl = values.terjemahanImage;
        }
      }

      const payload = {
        KategoriBudaya: values.kategoribudaya,
        Perkataan: values.perkataan,
        Denotasi: values.denotasi?.trim(),
        Konotasi: values.konotasi?.trim(),
        TafsiranUlama1: values.tafsiranUlama1?.trim(),
        TafsiranUlama2: values.tafsiranUlama2?.trim(),
      };

      // ✅ Handle optional fields correctly
      if (ayatUrl) payload.AyatAlQuran = ayatUrl;
      else payload.AyatAlQuran = null; // optional if you want to allow clearing the ayat video

      if (values.terjemahanayat?.trim()) {
        payload.TerjemahanAyat = values.terjemahanayat.trim();
      } else {
        payload.TerjemahanAyat = null;
      }

      if (terjemahanImageUrl) {
        payload.TerjemahanImage = terjemahanImageUrl;
      } else {
        // ✅ explicitly clear old image if none selected
        payload.TerjemahanImage = null;
      }

      const res = await api.put(`/perkataan/${id}`, payload);

      if (!res.data.success) {
        throw new Error(res.data.message || "Update failed");
      }

      setSnackbar({
        open: true,
        message: "Successfully updated perkataan!",
        severity: "success",
      });

      setTimeout(() => navigate("/"), 800);
    } catch (error) {
      console.error("Error updating perkataan:", error.message);
      setFieldError("submit", error.message);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update perkataan",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-screen mr-1 ml-1">
      <div className="flex-shrink-0">
        <HeaderLayout name="Edit Perkataan" />
      </div>

      {loading ? (
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner text="" />
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, errors, isValid, dirty }) => (
            <Form>
              <div className="overflow-y-auto h-[62vh] px-5 py-6 space-y-10 hide-scrollbar">
                {errors.submit && (
                  <div className="bg-red-100 text-red-700 p-3 rounded-md">
                    {errors.submit}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-12">
                  <SearchableDropdown
                    label="Kategori Budaya"
                    name="kategoribudaya"
                    requiredMark
                    options={kategoriBudayaOptions}
                    placeholder="Select Kategori Budaya"
                  />
                  <InputTextField
                    label="Perkataan"
                    name="perkataan"
                    requiredMark
                    placeholder="Enter Perkataan"
                    type="text"
                  />
                </div>

                <div className="grid grid-cols-2 gap-12">
                  <FileUpload
                    label="Ayat Al-Quran"
                    placeholder="Upload or drag MP4 file"
                    accept="video/mp4"
                    name="ayatalquran"
                  />
                  <div className="space-y-4">
                    <TextArea
                      label="Terjemahan Ayat"
                      placeholder="Write terjemahan ayat"
                      name="terjemahanayat"
                    />
                    <FileUpload
                      placeholder="Upload or drag translated verse image"
                      accept="image/*"
                      name="terjemahanImage"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-12">
                  <TextArea
                    label="Denotasi"
                    placeholder="Write Denotasi"
                    name="denotasi"
                  />
                  <TextArea
                    label="Konotasi"
                    placeholder="Write Konotasi"
                    name="konotasi"
                  />
                </div>

                <div className="grid grid-cols-2 gap-12">
                  <TextArea
                    label="Tafsiran Ulama 1 (Tafsir Al-Tahrir wa Al-Tanwir)"
                    placeholder="Write Tafsiran Ulama 1"
                    name="tafsiranUlama1"
                  />
                  <TextArea
                    label="Tafsiran Ulama 2 (Tafsir Al-Munir)"
                    placeholder="Write Tafsiran Ulama 2"
                    name="tafsiranUlama2"
                  />
                </div>
              </div>

              <div className="flex-shrink-0 flex items-center gap-4 justify-center pt-4 border-t bg-white">
                <Button
                  onClick={navigateBack}
                  className="w-[90px] h-[40px]"
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.buttonHover.main,
                    "&:hover": {
                      backgroundColor: theme.palette.buttonHover.secondary,
                    },
                    color: theme.palette.white.main,
                    textTransform: "none",
                  }}
                >
                  Back
                </Button>
                <FormikSubmitButton
                  name="Update"
                  disabled={isSubmitting || !isValid || !dirty}
                  className="w-[90px] h-[40px]"
                />
              </div>
            </Form>
          )}
        </Formik>
      )}

      {/* ✅ Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
